/**
 * One statement: a draft (worked out now) or an issued one, with what can
 * be done to it. The paper below the buttons is exactly what prints.
 */
import { useState } from 'react';
import {
  Alert, Anchor, Button, Group, Loader, Modal, Stack, Text, TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconPrinter } from '@tabler/icons-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  downloadStatementCsv, formatPence, useDraftStatement, useFinaliseStatement, useMarkPaid, useStatement,
  type Statement, type StatementKind,
} from '../../api/statements';
import { useNavUser } from '../../layout/navUser';
import { hasAction, isScopedUser } from '../../layout/nav';
import { StatementPaper } from './StatementPaper';

export function StatementViewPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const draftKind = (params.get('kind') as StatementKind | null) ?? null;
  const isDraft = id === 'draft';
  const stored = useStatement(isDraft ? null : id ?? null);
  const draft = useDraftStatement(
    isDraft ? draftKind : null,
    isDraft ? params.get('party') : null,
    isDraft ? params.get('period') : null,
  );
  const q = isDraft ? draft : stored;
  return (
    <Stack gap="md">
      <Anchor component={Link} to="/statements" size="sm" className="hf-no-print">Statements</Anchor>
      {q.error && <Alert color="red" variant="light">{q.error.message}</Alert>}
      {q.isPending && <Group justify="center" p="xl"><Loader /></Group>}
      {q.data && <StatementActions s={q.data} />}
      {q.data && <StatementPaper s={q.data} />}
    </Stack>
  );
}

function StatementActions({ s }: { s: Statement }) {
  const user = useNavUser();
  const staff = !isScopedUser(user) && hasAction(user, 'manage_billing');
  const navigate = useNavigate();
  const finalise = useFinaliseStatement();
  const paid = useMarkPaid();
  const [confirm, setConfirm] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [downloading, setDownloading] = useState(false);

  async function csv() {
    setDownloading(true);
    try {
      await downloadStatementCsv(
        s.id ? { id: s.id } : { kind: s.kind, partyId: s.party_id, period: s.period },
        `${s.number ?? `draft-${s.period}-${s.party_name}`}.csv`.replace(/[^\w.-]+/g, '-'),
      );
    } catch (e) {
      notifications.show({ color: 'red', title: 'Download failed', message: (e as Error).message });
    } finally {
      setDownloading(false);
    }
  }

  async function doFinalise() {
    try {
      const done = await finalise.mutateAsync({ kind: s.kind, party_id: s.party_id, period: s.period });
      notifications.show({ color: 'teal', title: `Issued as ${done.number}`, message: 'It is frozen now: later changes go on a later month.' });
      setConfirm(false);
      if (done.id) navigate(`/statements/${encodeURIComponent(done.id)}`, { replace: true });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not issued', message: (e as Error).message });
    }
  }

  async function doPaid(on: string | null) {
    if (!s.id) return;
    try {
      await paid.mutateAsync({ id: s.id, paidOn: on });
      setPaying(false);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not saved', message: (e as Error).message });
    }
  }

  return (
    <Group justify="space-between" className="hf-no-print" wrap="wrap">
      <Text size="sm" c="dimmed">
        {s.status === 'draft'
          ? s.finalise_blocked ?? 'A draft, worked out from the data as it is now.'
          : `Issued${s.finalised_by_name ? ` by ${s.finalised_by_name}` : ''}. It does not change.`}
      </Text>
      <Group gap="sm">
        <Button variant="default" leftSection={<IconDownload size={16} />} loading={downloading} onClick={csv}>
          Every line (CSV)
        </Button>
        <Button variant="default" leftSection={<IconPrinter size={16} />} onClick={() => window.print()}>
          Print or save as PDF
        </Button>
        {staff && s.status === 'draft' && (
          <Button disabled={!s.can_finalise} onClick={() => setConfirm(true)}>Issue statement</Button>
        )}
        {staff && s.id && s.status === 'finalised' && <Button onClick={() => setPaying(true)}>Mark paid</Button>}
        {staff && s.id && s.status === 'paid' && (
          <Button variant="default" loading={paid.isPending} onClick={() => doPaid(null)}>Mark unpaid</Button>
        )}
      </Group>

      <Modal opened={confirm} onClose={() => setConfirm(false)} title="Issue this statement?">
        <Stack>
          <Text size="sm">
            {`It gets the next number and is frozen at ${formatPence(s.totals.total_pence)}. A return approved from now on goes on a later month's statement.`}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirm(false)}>Cancel</Button>
            <Button loading={finalise.isPending} onClick={doFinalise}>Issue</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={paying} onClose={() => setPaying(false)} title="Mark as paid">
        <Stack>
          <TextInput label="Paid on" type="date" value={paidOn} onChange={(e) => setPaidOn(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setPaying(false)}>Cancel</Button>
            <Button loading={paid.isPending} disabled={!paidOn} onClick={() => doPaid(paidOn)}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </Group>
  );
}
